import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const EventInfo = ({ event }) => {
  return (
    <div className="space-y-8">
      {/* Event Description */}
      <section>
        <h2 className="text-2xl font-bold text-foreground mb-4">About This Event</h2>
        <div className="prose prose-slate max-w-none">
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
            {event?.description}
          </p>
        </div>
      </section>
      {/* Event Details */}
      <section>
        <h3 className="text-xl font-semibold text-foreground mb-4">Event Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Date & Time</h4>
                <p className="text-sm text-muted-foreground">{event?.date}</p>
                <p className="text-sm text-muted-foreground">{event?.time}</p>
                <p className="text-xs text-muted-foreground mt-1">{event?.timezone}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="MapPin" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Location</h4>
                <p className="text-sm text-muted-foreground">{event?.venue?.name}</p>
                <p className="text-sm text-muted-foreground">{event?.venue?.address}</p>
                <p className="text-sm text-muted-foreground">{event?.venue?.city}, {event?.venue?.state}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Capacity</h4>
                <p className="text-sm text-muted-foreground">{event?.totalCapacity} attendees</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {event?.totalCapacity - event?.bookedSeats} spots remaining
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Tag" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Category</h4>
                <p className="text-sm text-muted-foreground">{event?.category}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {event?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-xs text-muted-foreground rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Clock" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Duration</h4>
                <p className="text-sm text-muted-foreground">{event?.duration}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Doors open 30 minutes early
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Info" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Age Requirement</h4>
                <p className="text-sm text-muted-foreground">{event?.ageRequirement}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  ID required for verification
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Host Information */}
      <section>
        <h3 className="text-xl font-semibold text-foreground mb-4">Hosted By</h3>
        <div className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
          <Image
            src={event?.host?.avatar}
            alt={event?.host?.name}
            className="w-16 h-16 rounded-full object-cover"
          />
          <div className="flex-1">
            <h4 className="font-semibold text-foreground">{event?.host?.name}</h4>
            <p className="text-sm text-muted-foreground mb-2">{event?.host?.title}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {event?.host?.bio}
            </p>
            <div className="flex items-center space-x-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Icon name="Calendar" size={12} />
                <span>{event?.host?.eventsHosted} events hosted</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="Star" size={12} />
                <span>{event?.host?.rating} rating</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="Users" size={12} />
                <span>{event?.host?.followers} followers</span>
              </span>
            </div>
          </div>
        </div>
      </section>
      {/* Venue Map */}
      <section>
        <h3 className="text-xl font-semibold text-foreground mb-4">Venue Location</h3>
        <div className="bg-muted rounded-lg overflow-hidden">
          <div className="h-64 w-full">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title={event?.venue?.name}
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${event?.venue?.coordinates?.lat},${event?.venue?.coordinates?.lng}&z=15&output=embed`}
            />
          </div>
          <div className="p-4">
            <h4 className="font-medium text-foreground">{event?.venue?.name}</h4>
            <p className="text-sm text-muted-foreground">{event?.venue?.address}</p>
            <p className="text-sm text-muted-foreground">{event?.venue?.city}, {event?.venue?.state}</p>
            {event?.venue?.parking && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center space-x-1">
                <Icon name="Car" size={12} />
                <span>Parking available</span>
              </p>
            )}
          </div>
        </div>
      </section>
      {/* Policies */}
      <section>
        <h3 className="text-xl font-semibold text-foreground mb-4">Event Policies</h3>
        <div className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
              <Icon name="RotateCcw" size={16} className="text-primary" />
              <span>Cancellation Policy</span>
            </h4>
            <p className="text-sm text-muted-foreground">
              {event?.policies?.cancellation}
            </p>
          </div>
          
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
              <Icon name="RefreshCw" size={16} className="text-primary" />
              <span>Refund Policy</span>
            </h4>
            <p className="text-sm text-muted-foreground">
              {event?.policies?.refund}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventInfo;